import { Link } from 'react-router-dom'
import {ArrowRight, Sparkles, Wrench, Users, UserCheck, Droplets, Layers, HardHat, Hammer, Shield, Building2, Factory, ShoppingBag, Home as HomeIcon, Hotel, Star, CheckCircle2} from 'lucide-react'
import SectionHeading from '@/components/SectionHeading'
import TrustBadges from '@/components/TrustBadges'
import QuickContactForm from '@/components/QuickContactForm'
import { services } from '@/data/services'

const iconMap: Record<string, any> = {
  Sparkles, Shield, Wrench, Users, UserCheck, Droplets, Layers, HardHat, Hammer,
}

const industries = [
  { icon: HomeIcon, title: 'Strata & Residential' },
  { icon: Building2, title: 'Commercial Offices' },
  { icon: ShoppingBag, title: 'Retail & Shopping' },
  { icon: Factory, title: 'Industrial & Warehouse' },
  { icon: Hotel, title: 'Hospitality' },
]

const testimonials = [
  {
    quote: 'Delta transformed our portfolio maintenance. Consistent quality, transparent reporting, and teams who genuinely care.',
    name: 'Margaret Chen',
    role: 'VP Operations, Pacific Property Group',
  },
  {
    quote: 'After years of unreliable vendors, Delta delivered exactly what they promised — scale, consistency, and accountability.',
    name: 'David Thompson',
    role: 'Facility Director, Meridian Towers',
  },
  {
    quote: 'Their 24/7 response saved us during a critical post-construction deadline. True professionals end to end.',
    name: 'Sarah Patel',
    role: 'Project Manager, Horizon Developments',
  },
]

const stats = [
  { value: '500+', label: 'Properties Serviced' },
  { value: '12', label: 'Cities Covered' },
  { value: '24/7', label: 'Response Time' },
  { value: '98%', label: 'Client Retention' },
]

function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.lumi.new/04/04b7e26d643d95e3f1d5a7ef1580d0e0.png"
            alt="Modern commercial building Delta Facilities services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-8 fade-in">
              <span className="w-10 h-px bg-orange-accent" />
              <span className="text-xs uppercase tracking-[0.3em] text-orange-accent font-semibold">
                Nationwide Facility Services
              </span>
            </div>
            <h1 className="font-display text-white text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] fade-up">
              Canada's Trusted Partner in <span className="text-orange-accent">Facility Maintenance</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/85 leading-relaxed max-w-2xl fade-up" style={{ animationDelay: '0.15s' }}>
              Full-service cleaning, maintenance, and building support solutions tailored for commercial and residential properties.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-up" style={{ animationDelay: '0.3s' }}>
              <Link
                to="/quote"
                className="bg-orange-accent hover:bg-orange-dark text-white px-8 py-4 rounded-md font-semibold tracking-wide transition-all inline-flex items-center justify-center gap-2 group shadow-lg shadow-orange-accent/30"
              >
                Request a Quote
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white hover:text-navy text-white px-8 py-4 rounded-md font-semibold tracking-wide transition-all inline-flex items-center justify-center"
              >
                Our Services
              </Link>
            </div>

            {/* Stat strip */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 fade-up" style={{ animationDelay: '0.45s' }}>
              {stats.map((s) => (
                <div key={s.label} className="border-l-2 border-orange-accent/70 pl-4">
                  <div className="font-display text-3xl md:text-4xl font-bold text-white">{s.value}</div>
                  <div className="text-xs uppercase tracking-wider text-white/60 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="bg-grey-soft py-14 border-b border-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <TrustBadges />
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <SectionHeading
              eyebrow="Services"
              title="One partner for every facility need."
              description="From daily janitorial to post-construction cleaning, Delta delivers consistent, measurable results across every service line."
            />
            <Link to="/services" className="inline-flex items-center gap-2 text-orange-accent font-semibold text-sm link-underline whitespace-nowrap">
              Explore all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.slice(0, 8).map((service, i) => {
              const Icon = iconMap[service.icon] || Sparkles
              return (
                <Link
                  key={service.id}
                  to="/services"
                  className="group bg-white border border-soft rounded-xl p-7 hover-lift transition-all"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-navy group-hover:bg-orange-accent transition-colors flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-navy mb-2 leading-tight">{service.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{service.tagline}</p>
                  <div className="mt-5 flex items-center gap-1.5 text-xs font-semibold text-orange-accent uppercase tracking-wider">
                    Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE DELTA */}
      <section className="py-24 md:py-32 bg-grey-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative">
              <img
                src="https://static.lumi.new/0d/0d409a9ac280d82640f180118cd4b59c.png"
                alt="Professional Delta Facilities cleaning team at work"
                className="rounded-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -right-6 bg-navy text-white rounded-xl p-6 shadow-2xl hidden md:block max-w-[220px]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-orange-accent pulse-soft" />
                  <div className="text-xs uppercase tracking-wider text-white/70">Live Status</div>
                </div>
                <div className="font-display text-2xl font-bold">2,400+</div>
                <div className="text-sm text-white/70">service hours completed this week</div>
              </div>
            </div>

            <div>
              <SectionHeading
                eyebrow="Why Delta"
                title="Reliable teams. Measurable results."
                description="We're built on four pillars that set us apart in facility services."
              />

              <div className="mt-10 space-y-6">
                {[
                  { t: 'Reliability at Scale', d: 'Hundreds of properties served nationwide with consistent SLAs and transparent reporting.' },
                  { t: 'Trained Professionals', d: 'Every team member is background-checked, insured, and continuously trained to industry standards.' },
                  { t: '24/7 Support', d: 'Round-the-clock dispatch for emergencies, after-hours service, and critical maintenance needs.' },
                  { t: 'Measurable Accountability', d: 'Digital quality audits, inspection reports, and KPI dashboards — so you see the results.' },
                ].map((item) => (
                  <div key={item.t} className="flex gap-5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-accent/10 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-orange-accent" />
                    </div>
                    <div>
                      <h4 className="font-display text-lg font-bold text-navy mb-1">{item.t}</h4>
                      <p className="text-muted text-[15px] leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            centered
            eyebrow="Industries"
            title="Serving every environment, every time."
            description="Purpose-built programs for the industries and property types that trust Delta every day."
          />

          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {industries.map((industry) => {
              const Icon = industry.icon
              return (
                <div
                  key={industry.title}
                  className="group bg-white border border-soft rounded-xl p-7 text-center hover-lift transition-all"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-grey-soft group-hover:bg-orange-accent transition-colors flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-navy group-hover:text-white transition-colors" />
                  </div>
                  <h4 className="font-display text-base font-bold text-navy leading-tight">{industry.title}</h4>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-navy relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-96 h-96 rounded-full bg-orange-accent blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-orange-accent blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading
            centered light
            eyebrow="Client Voices"
            title="Trusted by Canada's leading property teams."
          />

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors"
              >
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-orange-accent text-orange-accent" />
                  ))}
                </div>
                <p className="text-white/90 text-[15px] leading-relaxed mb-6">"{t.quote}"</p>
                <div className="pt-5 border-t border-white/10">
                  <div className="font-semibold text-white text-sm">{t.name}</div>
                  <div className="text-white/60 text-xs mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK CONTACT */}
      <section className="py-24 md:py-32 bg-grey-soft">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <SectionHeading
                eyebrow="Get Started"
                title="Ready to simplify facility management?"
                description="Tell us about your property. We'll build a custom maintenance plan that fits your budget, timeline, and standards."
              />
              <div className="mt-8 space-y-3">
                {[
                  'No-obligation site assessment',
                  'Transparent, itemized pricing',
                  'Flexible month-to-month contracts',
                  'Dedicated account manager',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-accent flex-shrink-0" />
                    <span className="text-navy font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <QuickContactForm />
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
